import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import {
  Package,
  MagnifyingGlass,
  Boat,
  Airplane,
  Truck,
  MapPin,
  ArrowRight,
  CheckSquare,
  Square,
  DownloadSimple,
  Eye,
  Clock,
  Warning,
  CheckCircle,
  Cube,
  TrendUp,
  CircleNotch,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

interface Order {
  id: string
  status: string
  trackingNumber: string | null
  shippedAt: string | null
  estimatedDelivery: string | null
  actualDelivery: string | null
  createdAt: string
  updatedAt: string
  quoteTxControl: string
  origin: {
    location?: {
      city?: string
      country?: string
    }
  }
  destination: {
    location?: {
      city?: string
      country?: string
    }
  }
  modalityName: string
  agentName: string
  agentLogoUrl: string | null
  totalValue: number
  currency: string
}

interface Shipment {
  id: string
  trackingNumber: string
  modality: 'maritime' | 'air' | 'ground'
  status: 'in-transit' | 'at-port' | 'customs' | 'delivered' | 'delayed' | 'pending' | 'processing'
  origin: string
  destination: string
  estimatedArrival: string
  actualDeparture: string
  containerNumber?: string
  vesselName?: string
  flightNumber?: string
  truckPlate?: string
  weight?: string
  volume?: string
  progress: number
  carrier: string
  freightForwarder: string
  value: string
  lastUpdate: string
}

export default function ShipmentsPage() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterModality, setFilterModality] = useState('all')
  const [selectedShipments, setSelectedShipments] = useState<string[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch orders from API
  useEffect(() => {
    async function fetchOrders() {
      try {
        setLoading(true)
        setError(null)
        const response = await api.get('/shipper/orders')
        setOrders(response.data.orders || [])
      } catch (err: any) {
        console.error('Erro ao carregar embarques:', err)
        setError(err.response?.data?.error || 'Erro ao carregar embarques')
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()
  }, [])

  // Map modality name to type
  const mapModalityToType = (modalityName: string): 'maritime' | 'air' | 'ground' => {
    const lower = modalityName?.toLowerCase() || ''
    if (lower.includes('marit') || lower.includes('mar')) return 'maritime'
    if (lower.includes('aer') || lower.includes('air')) return 'air'
    if (lower.includes('terr') || lower.includes('road') || lower.includes('ground')) return 'ground'
    return 'maritime'
  }

  // Map order status to shipment status
  const mapOrderStatusToShipmentStatus = (status: string): Shipment['status'] => {
    const statusMap: Record<string, Shipment['status']> = {
      'pending': 'pending',
      'processing': 'processing',
      'in_transit': 'in-transit',
      'in-transit': 'in-transit',
      'at_port': 'at-port',
      'at-port': 'at-port',
      'customs': 'customs',
      'delivered': 'delivered',
      'delayed': 'delayed',
      'confirmed': 'processing',
      'shipped': 'in-transit',
    }
    return statusMap[status?.toLowerCase()] || 'pending'
  }

  // Calculate progress based on status
  const calculateProgress = (status: string): number => {
    const progressMap: Record<string, number> = {
      'pending': 10,
      'processing': 25,
      'confirmed': 30,
      'shipped': 50,
      'in_transit': 60,
      'in-transit': 60,
      'at_port': 75,
      'at-port': 75,
      'customs': 85,
      'delivered': 100,
      'delayed': 50,
    }
    return progressMap[status?.toLowerCase()] || 10
  }

  // Format currency
  const formatCurrency = (value: number, currency: string): string => {
    const currencySymbol = currency === 'USD' ? 'US$' : currency === 'EUR' ? '€' : 'R$'
    return `${currencySymbol} ${value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
  }

  // Transform orders to shipments format
  const shipments: Shipment[] = orders.map((order) => ({
    id: order.id,
    trackingNumber: order.trackingNumber || order.quoteTxControl,
    modality: mapModalityToType(order.modalityName),
    status: mapOrderStatusToShipmentStatus(order.status),
    origin: order.origin?.location?.city
      ? `${order.origin.location.city}${order.origin.location.country ? `, ${order.origin.location.country}` : ''}`
      : 'Origem não informada',
    destination: order.destination?.location?.city
      ? `${order.destination.location.city}${order.destination.location.country ? `, ${order.destination.location.country}` : ''}`
      : 'Destino não informado',
    estimatedArrival: order.estimatedDelivery || '',
    actualDeparture: order.shippedAt || '',
    progress: calculateProgress(order.status),
    carrier: order.agentName,
    freightForwarder: order.agentName,
    value: formatCurrency(order.totalValue, order.currency),
    lastUpdate: order.updatedAt,
  }))

  // Calculate metrics from real data
  const metrics = {
    overview: {
      total: orders.length,
      inTransit: orders.filter((o) =>
        ['in_transit', 'in-transit', 'shipped', 'at_port', 'at-port', 'customs'].includes(o.status?.toLowerCase())
      ).length,
      delivered: orders.filter((o) => o.status?.toLowerCase() === 'delivered').length,
    },
    onTime: {
      count: orders.filter((o) => o.status?.toLowerCase() !== 'delayed').length,
      percentage: orders.length > 0
        ? Math.round((orders.filter((o) => o.status?.toLowerCase() !== 'delayed').length / orders.length) * 100)
        : 0,
    },
    status: {
      atPort: orders.filter((o) => ['at_port', 'at-port'].includes(o.status?.toLowerCase())).length,
      customs: orders.filter((o) => o.status?.toLowerCase() === 'customs').length,
      delayed: orders.filter((o) => o.status?.toLowerCase() === 'delayed').length,
    },
  }

  // Filter functionality
  const filteredShipments = shipments.filter((shipment) => {
    const matchesSearch =
      shipment.trackingNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shipment.origin.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shipment.destination.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shipment.containerNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      shipment.vesselName?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = filterStatus === 'all' || shipment.status === filterStatus
    const matchesModality = filterModality === 'all' || shipment.modality === filterModality

    return matchesSearch && matchesStatus && matchesModality
  })

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedShipments.length === filteredShipments.length) {
      setSelectedShipments([])
    } else {
      setSelectedShipments(filteredShipments.map((s) => s.id))
    }
  }

  const toggleSelectShipment = (shipmentId: string) => {
    setSelectedShipments((prev) =>
      prev.includes(shipmentId)
        ? prev.filter((id) => id !== shipmentId)
        : [...prev, shipmentId]
    )
  }

  const isShipmentSelected = (shipmentId: string) => selectedShipments.includes(shipmentId)
  const isAllSelected =
    filteredShipments.length > 0 && selectedShipments.length === filteredShipments.length

  // Get modality icon
  const getModalityIcon = (modality: string) => {
    switch (modality) {
      case 'maritime':
        return <Boat size={18} className="text-blue-600" weight="duotone" />
      case 'air':
        return <Airplane size={18} className="text-purple-600" weight="duotone" />
      case 'ground':
        return <Truck size={18} className="text-green-600" weight="duotone" />
      default:
        return <Package size={18} />
    }
  }

  // Get status badge
  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      'pending': 'bg-gray-100 text-gray-700',
      'processing': 'bg-purple-100 text-purple-700',
      'in-transit': 'bg-blue-100 text-blue-700',
      'at-port': 'bg-yellow-100 text-yellow-700',
      'customs': 'bg-orange-100 text-orange-700',
      'delivered': 'bg-emerald-100 text-emerald-700',
      'delayed': 'bg-red-100 text-red-700',
    }

    const labels: Record<string, string> = {
      'pending': 'Pendente',
      'processing': 'Processando',
      'in-transit': 'Em Trânsito',
      'at-port': 'No Porto',
      'customs': 'Alfândega',
      'delivered': 'Entregue',
      'delayed': 'Atrasado',
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    )
  }

  // Calculate days until ETA
  const getDaysUntilETA = (eta: string) => {
    const days = Math.ceil(
      (new Date(eta).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    )
    if (days < 0) return 'Atrasado'
    if (days === 0) return 'Hoje'
    if (days === 1) return 'Amanhã'
    return `${days} dias`
  }

  // Get initials
  const getInitials = (name: string) => {
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Embarques
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {selectedShipments.length > 0 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-medium">{selectedShipments.length}</span>
                  selecionado{selectedShipments.length > 1 ? 's' : ''}
                </div>
              )}
              <Button
                variant="outline"
                size="default"
                className="gap-2"
              >
                <DownloadSimple size={18} />
                Exportar
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics Zone - 3 Column Grid */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Overview Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Embarques Overview
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-gray-900">{metrics.overview.total}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {metrics.overview.inTransit} em trânsito, {metrics.overview.delivered} entregues
                </p>
              </div>
              <Package size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* On Time Performance Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Pontualidade
            </p>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">{metrics.onTime.percentage}%</p>
                <p className="text-sm text-gray-600 mt-1">
                  {metrics.onTime.count} no prazo
                </p>
              </div>
              <CheckCircle size={32} className="text-emerald-600 opacity-80" weight="duotone" />
            </div>
          </div>

          {/* Status Breakdown Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
              Status Atual
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">No Porto</span>
                <span className="font-semibold text-gray-900">{metrics.status.atPort}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Alfândega</span>
                <span className="font-semibold text-gray-900">{metrics.status.customs}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 flex items-center gap-1">
                  <Warning size={14} className="text-red-500" />
                  Atrasados
                </span>
                <span className="font-semibold text-red-600">{metrics.status.delayed}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar - Horizontal Compact */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por número de rastreamento, origem, destino ou container..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-9 border-gray-200"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todos Status</option>
              <option value="pending">Pendente</option>
              <option value="processing">Processando</option>
              <option value="in-transit">Em Trânsito</option>
              <option value="at-port">No Porto</option>
              <option value="customs">Alfândega</option>
              <option value="delivered">Entregue</option>
              <option value="delayed">Atrasado</option>
            </select>

            {/* Modality Filter */}
            <select
              value={filterModality}
              onChange={(e) => setFilterModality(e.target.value)}
              className="px-3 h-9 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">Todas Modalidades</option>
              <option value="maritime">Marítimo</option>
              <option value="air">Aéreo</option>
              <option value="ground">Terrestre</option>
            </select>

            {/* Export */}
            <Button variant="outline" size="sm" className="gap-2 h-9">
              <DownloadSimple size={16} />
              Exportar
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white border border-gray-200 rounded-lg p-12">
            <div className="flex flex-col items-center justify-center">
              <CircleNotch size={48} className="text-emerald-600 animate-spin mb-4" />
              <p className="text-gray-600">Carregando embarques...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-white border border-red-200 rounded-lg p-12">
            <div className="flex flex-col items-center justify-center">
              <Warning size={48} className="text-red-500 mb-4" />
              <p className="text-red-600 font-medium mb-2">Erro ao carregar embarques</p>
              <p className="text-gray-600 text-sm mb-4">{error}</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="gap-2"
              >
                Tentar novamente
              </Button>
            </div>
          </div>
        )}

        {/* Data Grid */}
        {!loading && !error && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <button
                      onClick={toggleSelectAll}
                      className="flex items-center justify-center w-full"
                    >
                      {isAllSelected ? (
                        <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                      ) : (
                        <Square size={18} className="text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Embarque
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rota
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transportador
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ETD
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ETA
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progresso
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredShipments.map((shipment) => (
                  <tr
                    key={shipment.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      isShipmentSelected(shipment.id) ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleSelectShipment(shipment.id)}
                        className="flex items-center justify-center"
                      >
                        {isShipmentSelected(shipment.id) ? (
                          <CheckSquare size={18} weight="fill" className="text-emerald-600" />
                        ) : (
                          <Square size={18} className="text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center flex-shrink-0">
                          {getModalityIcon(shipment.modality)}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">
                            {shipment.trackingNumber}
                          </p>
                          <p className="text-xs text-gray-500 font-mono truncate">
                            {shipment.containerNumber || shipment.flightNumber || shipment.truckPlate}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 truncate max-w-[100px]" title={shipment.origin}>
                          {shipment.origin.split(',')[0]}
                        </span>
                        <ArrowRight size={14} className="text-gray-400 flex-shrink-0" />
                        <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                        <span className="text-gray-900 truncate max-w-[100px]" title={shipment.destination}>
                          {shipment.destination.split(',')[0]}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {shipment.carrier}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {shipment.vesselName || shipment.flightNumber || shipment.truckPlate}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(shipment.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">
                        {shipment.actualDeparture
                          ? new Date(shipment.actualDeparture).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                          : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-900">
                            {shipment.estimatedArrival
                              ? new Date(shipment.estimatedArrival).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                              : '—'}
                          </p>
                          {shipment.estimatedArrival && (
                            <p className={`text-xs ${shipment.status === 'delayed' ? 'text-red-600' : 'text-gray-500'}`}>
                              {getDaysUntilETA(shipment.estimatedArrival)}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 max-w-[80px]">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{
                                width: `${shipment.progress}%`,
                                backgroundColor:
                                  shipment.status === 'delayed' ? 'rgb(239, 68, 68)' :
                                  shipment.status === 'delivered' ? 'rgb(16, 185, 129)' :
                                  'rgb(59, 130, 246)',
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-xs font-medium text-gray-600 min-w-[35px] text-right">
                          {shipment.progress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/shipper/orders/${shipment.id}`)}
                          className="h-8 w-8 p-0"
                          title="Ver Detalhes"
                        >
                          <Eye size={16} className="text-gray-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredShipments.length === 0 && (
            <div className="text-center py-12 bg-gray-50">
              <Package size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
              <p className="text-gray-500 text-sm">Nenhum embarque encontrado</p>
            </div>
          )}

          {/* Footer with pagination */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{filteredShipments.length}</span> embarques
              {selectedShipments.length > 0 && (
                <>
                  {' '}
                  · <span className="font-medium">{selectedShipments.length}</span> selecionado
                  {selectedShipments.length > 1 ? 's' : ''}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select className="px-2 py-1 border border-gray-200 rounded text-sm bg-white">
                <option>50 por página</option>
                <option>100 por página</option>
                <option>200 por página</option>
              </select>
              <span className="text-sm text-gray-600">Página 1 de 1</span>
            </div>
          </div>
        </div>
        )}
      </main>
    </AppLayout>
  )
}
